import { Group, Title } from "@mantine/core";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { SplitButton } from "shared/components/global/SplitButton";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    composeFormStateToDraftInfoInput,
    useComposeForm,
} from "shared/components/message/compose/context";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { TemplateSearchRepository } from "shared/components/message/templates/data";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { features } from "shared/graphql/features";
import { useFormState } from "shared/hooks/useFormState";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { catching } from "shared/utils/fns";
import { some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Cm, MessageSeverity } from "src/gql/graphql";
import { container } from "tsyringe";

const Component = () => {
    const hasCmOverrides = features.enabled("HAS_CM_DELIVERY_OVERRIDES");

    const nav = useNavigate();
    const form = useComposeForm({
        validate: zodResolver(composeFormStateSchema),
        initialValues: {
            audience: {
                groups: [],
                topics: [],
                users: [],
                features: [],
                geofilters: [],
            },
            customizations: {
                sms: { enabled: false, value: "" },
                phone: { enabled: false, value: "" },
            },
            duration: { span: 1440, absolute: null },
            acknowledgable: false,
            escalations: [],
            severity: MessageSeverity.Informational,
            sender: { id: "" },
            body: "",
            richBody: "",
            options: hasCmOverrides ? [{ method: Cm.Email }] : Object.values(Cm).map((it) => ({ method: it })),
            files: [],
            subject: "",
            meta: { mode: "edit" },
        },
    });

    const actions = useMessageActions();
    const [senderId] = useFormState(form, "sender.id");

    const { data: senderData } = useQuery(queries.senderOwner(senderId));
    const create = useGraphqlMutation(mutations.create);

    const repo = container.resolve(TemplateSearchRepository);

    function handleCreate() {
        if (!senderData) return;
        if (form.validate().hasErrors) return;

        some(form.getValues())
            .map(composeFormStateToDraftInfoInput.parse)
            .take((message) => create.mutateAsync({ info: { message } }))
            .then((t) => repo.upsert(t.createTemplateV2))
            .then(() => qk.invalidate("templates"))
            .then(() => nav({ to: "/messages/templates" }))
            .then(() => notify.show.success("Template Created!"))
            .catch(notify.catch);
    }

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>New Template</Title>
            </Content.Heading>

            <ComposeFormProvider form={form}>
                <ComposeForm />
            </ComposeFormProvider>

            <Group justify="flex-end">
                <SplitButton onClick={handleCreate} menu={{ "Send Preview": { fn: () => actions.openPreviewModal({ form }) } }}>
                    Create Template
                </SplitButton>
            </Group>
        </Content>
    );
};

const queries = {
    senderOwner: (senderId: string) =>
        queryOptions({
            queryKey: qk("templates", "senderOwner", senderId),
            throwOnError: false,
            queryFn: () =>
                catching(() =>
                    httpPostGraphql(
                        graphql(`
                            query TemplateCreateSenderOwner($senderId: UUID!) {
                                sender(id: $senderId) {
                                    group {
                                        id
                                    }
                                }
                            }
                        `),
                        { senderId },
                    ),
                ),
            select: (data) => data?.sender,
        }),
};

const mutations = {
    create: graphql(`
        mutation TemplateCreate($info: TemplateInfoV2Input!) {
            createTemplateV2(info: $info) {
                ...TemplateLike
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/templates/create")({
    component: Component,
});
